const DEFAULT_RETRYABLE_STATUSES = [502, 503, 504];

/**
 * @param {unknown} error
 * @returns {boolean}
 */
export function isNetworkFailure(error) {
  if (!error || typeof error !== "object") return false;
  if (error.name === "AbortError") return false;

  const message = String(error.message || "");
  return (
    error.name === "TypeError" &&
    (message.includes("Failed to fetch") || message.includes("NetworkError"))
  );
}

/**
 * @param {number} status
 * @param {number[]} [retryableStatuses]
 * @returns {boolean}
 */
export function isRetryableHttpStatus(status, retryableStatuses = DEFAULT_RETRYABLE_STATUSES) {
  return retryableStatuses.includes(status);
}

/**
 * @param {number} attempt - 1-based attempt number before the next retry
 * @param {number} [baseMs]
 * @returns {number}
 */
export function getRetryDelayMs(attempt, baseMs = 500) {
  if (attempt < 1) return baseMs;
  return baseMs * 2 ** (attempt - 1);
}

/**
 * @param {number} ms
 * @param {AbortSignal} [signal]
 * @returns {Promise<void>}
 */
export function sleep(ms, signal) {
  return new Promise((resolve, reject) => {
    if (signal?.aborted) {
      reject(signal.reason ?? new DOMException("Aborted", "AbortError"));
      return;
    }

    const timeoutId = setTimeout(() => {
      cleanup();
      resolve();
    }, ms);

    const onAbort = () => {
      cleanup();
      reject(signal?.reason ?? new DOMException("Aborted", "AbortError"));
    };

    const cleanup = () => {
      clearTimeout(timeoutId);
      signal?.removeEventListener("abort", onAbort);
    };

    signal?.addEventListener("abort", onAbort, { once: true });
  });
}

/**
 * Fetch with retry for transient network failures and gateway errors.
 * @param {typeof fetch} fetchImpl
 * @param {string|URL|Request} url
 * @param {RequestInit} [options]
 * @param {object} [config]
 * @returns {Promise<Response>}
 */
export async function fetchWithRetry(fetchImpl, url, options = {}, config = {}) {
  const maxAttempts = config.maxAttempts ?? 3;
  const baseDelayMs = config.baseDelayMs ?? 500;
  const retryableStatuses = config.retryableStatuses ?? DEFAULT_RETRYABLE_STATUSES;
  const signal = options.signal;
  let lastError = null;
  let lastResponse = null;

  for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
    if (signal?.aborted) {
      throw signal.reason ?? new DOMException("Aborted", "AbortError");
    }

    try {
      const response = await fetchImpl(url, options);

      if (!isRetryableHttpStatus(response.status, retryableStatuses) || attempt >= maxAttempts) {
        return response;
      }

      lastResponse = response;
      lastError = null;
    } catch (error) {
      lastError = error;
      lastResponse = null;

      if (!isNetworkFailure(error) || attempt >= maxAttempts) {
        throw error;
      }
    }

    if (attempt >= maxAttempts) {
      break;
    }

    if (typeof config.onRetry === "function") {
      await config.onRetry({
        attempt: attempt + 1,
        maxAttempts,
        error: lastError ?? undefined,
        status: lastResponse?.status,
      });
    }

    if (signal?.aborted) {
      throw signal.reason ?? new DOMException("Aborted", "AbortError");
    }

    await sleep(getRetryDelayMs(attempt, baseDelayMs), signal);
  }

  if (lastResponse) {
    return lastResponse;
  }

  throw lastError ?? new TypeError("Failed to fetch");
}
